// src/components/analyze/AnalogGeneratorTab.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Space, message } from 'antd';
import { useSocketContext } from '../../sockets/SocketContext';
import { useDispatch, useSelector } from 'react-redux';
import { generateAnalogs, similarAnalogSearchStart } from '../../store/analogGeneratorSlice';
import AnalogSuggestionCard from './AnalogGenerator/AnalogSuggestionCard';
import MktuClassSelector from '../common/MktuClassSelector';

const AnalogGeneratorTab = () => {
  const dispatch = useDispatch();
  const { socket, createNewTaskOnServer } = useSocketContext();

  // Состояние для ввода бренда
  const [brandName, setBrandName] = useState('');
  // Состояние для выбора класса МКТУ
  const [mktu, setMktu] = useState([]);
  
  // Локальное состояние для предложенных аналогов (карточки)
  // Каждая карточка: { id, text, locked, similarRequested }
  const [analogs, setAnalogs] = useState([]);
  const processedAnalogIdsRef = useRef(new Set());
  // Счетчик для уникальных id карточек
  const analogIdCounter = useRef(0);
  const generateId = () => {
    analogIdCounter.current++;
    return `analog-${analogIdCounter.current}`;
  };

  // Выбираем данные из Redux
  const analogState = useSelector((state) => state.analogGenerator);

  // Обновление карточек при получении данных из Redux
  useEffect(() => {
    if (analogState.analogs && analogState.analogs.length > 0) {
      setAnalogs((prev) => {
        // Сохраняем заблокированные карточки
        const locked = prev.filter((a) => a.locked);
        // Формируем новые карточки из полученных аналогов и добавляем флаг similarRequested: false
        const newUnlocked = analogState.analogs.slice(0, 10).map((text) => ({
          id: generateId(),
          text,
          locked: false,
          similarRequested: false,
        }));
        return [...locked, ...newUnlocked];
      });
    }
  }, [analogState.analogs]);

  const similarState = useSelector((state) => state.analogGenerator.similar);

  useEffect(() => {
    // Для каждой карточки, если запрос еще не отправлен (и в сторе нет записи),
    // отправляем запрос и отмечаем её как обработанную.
    analogs.forEach((analog) => {
      if (
        !processedAnalogIdsRef.current.has(analog.id) &&
        !similarState[analog.id]
      ) {
        // Отмечаем, что для этой карточки запрос уже отправлен
        processedAnalogIdsRef.current.add(analog.id);
  
        // Обновляем стор, чтобы зафиксировать, что запрос отправлен
        dispatch(similarAnalogSearchStart(analog.id));
  
        // Отправляем запрос через createNewTaskOnServer для поиска похожих аналогов
        createNewTaskOnServer(
          {
            similar_analog: analog.text,
            quantity: 5,
            suggestionId: analog.id,
            mktu: mktu,
          },
          'similar_analog',
          (err, task) => {
            if (err) {
              message.error('Ошибка создания задачи для поиска похожих аналогов');
            }
          }
        );
      }
    });
  // mktu намеренно не в deps: перезапуск только при изменении списка аналогов
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analogs, similarState, dispatch, createNewTaskOnServer]);

  // Функция переключения замка у карточки
  const toggleLock = (analog) => {
    setAnalogs((prev) =>
      prev.map((item) =>
        item.id === analog.id ? { ...item, locked: !item.locked } : item
      )
    );
  };

  // Функция очистки незаблокированных карточек
  const clearAnalogs = () => {
    setAnalogs((prev) => prev.filter((item) => item.locked));
  };

  // Генерация аналогов
  const generateAnalogsForBrand = () => {
    if (!brandName) {
      message.error('Введите название бренда для поиска аналогов');
      return;
    }
    // Перед новым запросом оставляем только заблокированные карточки
    setAnalogs((prev) => prev.filter((item) => item.locked));
    dispatch(
      generateAnalogs({
        brandName,
        mktu,
        socket,
      })
    );
  };

  return (
    <div style={{ padding: 20 }}>
      {/* Блок формы с ограниченной шириной */}
      <div style={{ maxWidth: '500px' }}>
        <h3 style={{ marginTop: 20 }}>Обозначение</h3>
        <Form layout="vertical" style={{ textAlign: 'left' }}>
            <Input.TextArea
              placeholder="Введите название бренда для поиска аналогов"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          <Form.Item label="Класс МКТУ">
            <MktuClassSelector 
              mode="multiple" 
              placeholder="Выберите классы" 
              value={mktu}
              onChange={setMktu}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={generateAnalogsForBrand} loading={analogState.loading}>
                Доработать
              </Button>
              <Button onClick={clearAnalogs}>Очистить</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      {/* Блок результатов, занимающий всю ширину */}
      <div style={{ marginTop: 30 }}>
        <h3>Варианты обозначения</h3>
        {analogs.length > 0 ? (
          analogs.map((analog) => (
            <AnalogSuggestionCard
              key={analog.id}
              analog={analog}
              onToggleLock={toggleLock}
            />
          ))
        ) : (
          <p>Нет результатов</p>
        )}
      </div>
    </div>
  );
};

export default AnalogGeneratorTab; 